import React, { useCallback, MouseEventHandler } from 'react';
import { TreeItem } from '@material-ui/lab';
import classNames from 'classnames';
import './TreeNode.less';
import { TreeNodeContent, ITreeNode, ITreeNodeContentProps } from '../CommonTree/CommonTreeNode';
import { ITreeScroll, ScrollType } from './Tree';
import { SkeletonTree } from 'loaders/SkeletonTree';

export enum NodePosition {
  first,
  middle,
  last,
}

function getTreeNodeClassNames(
  expanded?: boolean,
  isLeaf?: boolean,
  baseClassName?: string,
  position?: NodePosition,
  isRoot?: boolean
) {
  return classNames(
    `dpc-tree-node`,
    { 'dpc-tree-node--expanded': expanded },
    { 'dpc-tree-node--leaf': isLeaf },
    { 'dpc-tree-node--root': isRoot },
    { 'dpc-tree-node--first': position === NodePosition.first },
    { 'dpc-tree-node--last': position === NodePosition.last },
    baseClassName
  );
}

export interface ITreeNodeCallbacks<T> {
  onNodeExpand?: (node: ITreeNode<T>) => Promise<ITreeNode<T> | null | void> | void;
  onNodeFold?: (node: ITreeNode<T>) => Promise<ITreeNode<T> | null | void> | void;
  onNodeSelect?: (node: ITreeNode<T>) => Promise<ITreeNode<T> | null | void> | void;
  onNodeKeyDown?: (node: ITreeNode<T>, e: React.KeyboardEvent) => void;
  onNodeKeyUp?: (node: ITreeNode<T>, e: React.KeyboardEvent) => void;
  onNodeMouseDown?: (node: ITreeNode<T>, e: React.MouseEvent) => void;
  onNodeMouseUp?: (node: ITreeNode<T>, e: React.MouseEvent) => void;
  onNodeMouseOver?: (node: ITreeNode<T>, e: React.MouseEvent) => void;
  onNodeMouseOut?: (node: ITreeNode<T>, e: React.MouseEvent) => void;
  onNodeContextMenu?: (node: ITreeNode<T>, e: React.MouseEvent) => void;
}

export interface ITreeNodeProps<T> extends ITreeNode<T>, ITreeNodeCallbacks<T> {
  classes?: {
    content?: string;
  };
  contentComponent?: (props: React.PropsWithChildren<ITreeNodeContentProps<T>>) => JSX.Element;
  isRoot?: boolean;
  searchString?: string;
  scroll?: ITreeScroll;
  nodePosition?: NodePosition;
  sortNodes?: (a: ITreeNode<T>, b: ITreeNode<T>) => number;
}

export function TreeNode<T>(props: ITreeNodeProps<T>) {
  const {
    isRoot,
    isExpanded,
    childNodes,
    contentComponent,
    hasChildren,
    nodePosition,
    onNodeKeyDown,
    onNodeSelect,
    onNodeExpand,
    onNodeFold,
    onNodeMouseDown,
    onNodeMouseUp,
    sortNodes,
    onNodeKeyUp,
    onNodeMouseOver,
    onNodeMouseOut,
    onNodeContextMenu,
    classes,
    scroll,
    searchString,
    ...otherProps
  } = props;

  const Content = contentComponent ?? TreeNodeContent;

  const onNodeKeyDownEvent = useCallback(
    (e: React.KeyboardEvent) => {
      onNodeKeyDown?.(props, e);

      if (e.key === 'Enter' && props.isSelectable) {
        onNodeSelect?.(props);
      }
      if (e.key === 'ArrowRight' && props.hasChildren && !props.isExpanded) {
        onNodeExpand?.(props);
      }
      if (e.key === 'ArrowLeft' && props.hasChildren && props.isExpanded) {
        onNodeFold?.(props);
      }

      e.stopPropagation();
      e.preventDefault();
    },
    [onNodeKeyDown, props, onNodeSelect, onNodeExpand, onNodeFold]
  );

  const handleMouseDown: React.MouseEventHandler = useCallback(
    event => {
      if (onNodeMouseDown) {
        onNodeMouseDown(props, event);
        event.stopPropagation();
      }
    },
    [onNodeMouseDown, props]
  );

  const handleMouseUp: React.MouseEventHandler = useCallback(
    event => {
      if (onNodeMouseUp) {
        onNodeMouseUp(props, event);
        event.stopPropagation();
      }
    },
    [onNodeMouseUp, props]
  );

  const handleMouseOut: React.MouseEventHandler = useCallback(
    event => {
      if (onNodeMouseOut) {
        onNodeMouseOut(props, event);
        event.stopPropagation();
      }
    },
    [onNodeMouseOut, props]
  );

  const handleMouseOver: React.MouseEventHandler = useCallback(
    event => {
      if (onNodeMouseOver) {
        onNodeMouseOver(props, event);
        event.stopPropagation();
      }
    },
    [onNodeMouseOver, props]
  );

  const handleNodeClick = useCallback(
    (event: React.MouseEvent) => {
      if (props.isSelectable) {
        onNodeSelect?.(props);
      }
      event.stopPropagation();
    },
    [onNodeSelect, props]
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      if (onNodeContextMenu) {
        onNodeContextMenu(props, event);
      }
    },
    [onNodeContextMenu, props]
  );

  const handleNodeExpand: MouseEventHandler<HTMLButtonElement> = useCallback(
    async event => {
      if (isExpanded) {
        onNodeFold?.(props);
      } else {
        let parent: HTMLElement | null = null;
        if (scroll?.scrollType !== ScrollType.none) {
          parent = (event.target as HTMLButtonElement).closest('.dpc-tree-node');
        }
        await onNodeExpand?.(props);

        if (parent && scroll?.scrollType === ScrollType.auto) {
          scrollToNode(parent);
        }
        if (parent && scroll?.scrollType === ScrollType.custom) {
          scroll.customCallback?.(parent);
        }
      }
    },
    [isExpanded, onNodeExpand, onNodeFold, props, scroll]
  );

  return (
    <TreeItem
      classes={{
        group: 'dpc-tree-node__children',
        content: 'dpc-tree-node__item',
        iconContainer: 'dpc-tree-node__icon-container',
      }}
      nodeId={props.nodeId}
      label={
        <Content
          {...otherProps}
          nodeId={props.nodeId}
          name={props.name}
          actions={props.actions}
          subActions={props.subActions}
          icon={props.icon}
          description={props.description}
          hasChildren={props.hasChildren}
          data={props.data}
          childNodes={props.childNodes}
          isActive={props.isActive}
          isExpanded={props.isExpanded}
          isLoading={false}
          searchString={searchString}
          onClick={handleNodeClick}
          onExpandNodeClick={handleNodeExpand}
          onMouseUp={handleMouseUp}
          onMouseDown={handleMouseDown}
          onMouseOut={handleMouseOut}
          onMouseOver={handleMouseOver}
          onContextMenu={handleContextMenu}
        />
      }
      className={getTreeNodeClassNames(isExpanded, !hasChildren, classes?.content, nodePosition, isRoot)}
      onKeyDown={onNodeKeyDownEvent}
    >
      {props.isLoading ? (
        <SkeletonTree
          model={
            new Map([
              ['1', 0],
              ['2', 0],
            ])
          }
        />
      ) : (
        (sortNodes ? childNodes?.sort(sortNodes) : childNodes)?.map((node, i, arr) => {
          return (
            <TreeNode
              key={node.nodeId}
              nodePosition={getNodePosition(i, arr.length)}
              onNodeKeyDown={onNodeKeyDown}
              onNodeKeyUp={onNodeKeyUp}
              contentComponent={Content}
              onNodeExpand={onNodeExpand}
              onNodeFold={onNodeFold}
              onNodeSelect={onNodeSelect}
              onNodeMouseDown={onNodeMouseDown}
              onNodeMouseUp={onNodeMouseUp}
              onNodeMouseOver={onNodeMouseOver}
              onNodeMouseOut={onNodeMouseOut}
              onNodeContextMenu={onNodeContextMenu}
              sortNodes={sortNodes}
              classes={classes}
              searchString={searchString}
              scroll={scroll}
              {...node}
            />
          );
        })
      )}
    </TreeItem>
  );
}

export function getNodePosition(index: number, arrayLength: number): NodePosition {
  if (index === 0) {
    return NodePosition.first;
  } else if (index === arrayLength - 1) {
    return NodePosition.last;
  } else {
    return NodePosition.middle;
  }
}

export function scrollToNode(element: HTMLElement) {
  // scroll after expanding delay
  setTimeout(() => {
    const elemHeight = element?.offsetHeight || 0; // offset height, not offset top?
    const windowHeight = window.innerHeight || 0;
    if (elemHeight > windowHeight * 0.75) {
      element?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, 300);
}
